import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { NODES, TERRAIN, FORAGE } from '../config';
import { terrainHeight } from '../engine/terrain';
import { biomeAt, Biome } from '../engine/biome';
import { applyInteriorDark } from '../engine/interior';
import type { ItemId } from './inventory';

// Forageable plants: mushroom clusters (forest/grassland), pumpkins + corn (near inland rivers/lakes). Walk
// up and press E to collect (handled by the main interact dispatch via nearestPickable + collect). Each plant
// is one slot in a fixed, deterministic scatter; collecting it hides the slot and starts a REGROW timer, so a
// region tops back up over time and cannot be permanently stripped. Instanced + distance-streamed like the
// trees/bushes so only nearby plants draw. Cosmetic geometry; no collider (you walk through/over them).

const F_CELL = 48, F_OFF = 4096, F_STRIDE = 8192;
const UP = new THREE.Vector3(0, 1, 0);
const FORAGE_SEED = 20260624;

export type ForageKind = 'mushroom' | 'pumpkin' | 'corn' | 'coconut' | 'apple' | 'hemp';
interface KindDef { item: ItemId; amount: number; geom: THREE.BufferGeometry; }

// One picked-and-not-yet-regrown plant in the world save. Keyed by POSITION (decimetre-rounded), not
// slot index: the deterministic fill() gives stable indices for the same seed+config, but #test
// seedNear() appends extra slots and any config tweak reorders them, while positions either match
// exactly (same Float32 math on reload) or harmlessly miss (plant just regrows). `rem` is the
// REMAINING regrow seconds at save time; the boot clock restarts at ~0, so absolute times are useless.
export interface SavedForage { k: string; rem: number; }
function fKey(x: number, z: number): string { return `${Math.round(x * 10)},${Math.round(z * 10)}`; }

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

// --- low-poly geometries -------------------------------------------------------------------------------
function makeMushroomCluster(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const one = (x: number, z: number, s: number): void => {
    const stem = new THREE.CylinderGeometry(0.03 * s, 0.04 * s, 0.18 * s, 5); stem.translate(x, 0.09 * s, z);
    const cap = new THREE.SphereGeometry(0.1 * s, 7, 4, 0, Math.PI * 2, 0, Math.PI / 2); cap.scale(1, 0.7, 1); cap.translate(x, 0.18 * s, z);
    parts.push(stem, cap);
  };
  one(0, 0, 1.0); one(0.12, 0.05, 0.7); one(-0.1, 0.08, 0.6);
  const g = mergeGeometries(parts, false)!; for (const p of parts) p.dispose(); g.computeVertexNormals();
  g.scale(1.35, 1.35, 1.35); // chunky enough to spot from a stride away
  return g;
}
function makePumpkin(): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(0.32, 9, 6); g.scale(1, 0.72, 1); g.translate(0, 0.23, 0); g.computeVertexNormals(); return g;
}
// A couple of coconuts resting on the sand at a palm's foot (brown husks, one split open pale).
// Paint one part a solid colour via a vertex-colour attribute, so a merged model can be multi-coloured
// while staying ONE instanced draw (the kind's material sets vertexColors and a white base).
function tint(g: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const c = new THREE.Color(hex);
  const n = g.getAttribute('position').count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { arr[i * 3] = c.r; arr[i * 3 + 1] = c.g; arr[i * 3 + 2] = c.b; }
  g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return g;
}

function makeCoconut(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const nut = (x: number, z: number, s: number, yaw: number): void => {
    const shell = new THREE.SphereGeometry(0.14 * s, 8, 7); shell.scale(1, 1.06, 0.96); // slightly oval husk
    tint(shell, 0x7a5836); shell.rotateY(yaw); shell.translate(x, 0.14 * s, z); parts.push(shell);
    const band = new THREE.TorusGeometry(0.115 * s, 0.02 * s, 5, 10); // fibrous husk ridge
    tint(band, 0x5c3f24); band.rotateX(Math.PI / 2); band.rotateY(yaw); band.translate(x, 0.14 * s, z); parts.push(band);
    for (let e = 0; e < 3; e++) { // the three germination "eyes" that make it read COCONUT
      const eye = new THREE.SphereGeometry(0.016 * s, 4, 4);
      tint(eye, 0x2e2015);
      const a = e * 0.5 - 0.5;
      eye.translate(x + Math.sin(a) * 0.055 * s, 0.14 * s + 0.125 * s, z + Math.cos(a) * 0.055 * s);
      parts.push(eye);
    }
  };
  nut(0, 0, 1.0, 0.3); nut(0.24, 0.07, 0.85, 2.1); nut(-0.15, 0.19, 0.8, 4.4);
  const g = mergeGeometries(parts, false)!; for (const p of parts) p.dispose(); g.computeVertexNormals(); return g;
}
// A small pile of fallen apples: glossy red fruit, a short brown stem and one green leaf each.
function makeApple(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const one = (x: number, z: number, s: number): void => {
    const body = new THREE.SphereGeometry(0.095 * s, 9, 8);
    body.scale(1, 0.9, 1);
    // classic apple dimple: pinch the top ring slightly inward
    const pos = body.getAttribute('position');
    for (let i = 0; i < pos.count; i++) { if (pos.getY(i) > 0.07 * s) pos.setY(i, pos.getY(i) - 0.012 * s); }
    tint(body, 0xd23b2c); body.translate(x, 0.09 * s, z); parts.push(body);
    const stem = new THREE.CylinderGeometry(0.008 * s, 0.01 * s, 0.05 * s, 4);
    tint(stem, 0x5a3a1e); stem.translate(x, 0.19 * s, z); parts.push(stem);
    const leaf = new THREE.PlaneGeometry(0.05 * s, 0.028 * s);
    tint(leaf, 0x4f7a34); leaf.rotateX(-0.9); leaf.rotateY(0.7); leaf.translate(x + 0.025 * s, 0.2 * s, z); parts.push(leaf);
  };
  one(0, 0, 1.0); one(0.17, 0.05, 0.85); one(-0.11, 0.14, 0.78);
  const g = mergeGeometries(parts, false)!; for (const p of parts) p.dispose(); g.computeVertexNormals(); return g;
}
// A HEMP plant: a slim stalk with a fan of tall serrated leaves + a top frond. Picked for CLOTH (not a stored
// item itself). Reads as a bushy green weed from a stride away.
function makeHemp(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const stalk = new THREE.CylinderGeometry(0.02, 0.035, 0.72, 5); stalk.translate(0, 0.36, 0); parts.push(stalk);
  for (let i = 0; i < 7; i++) {
    const leaf = new THREE.PlaneGeometry(0.15, 0.46 + (i % 3) * 0.08);
    leaf.rotateX(-0.5); leaf.rotateY(i * (Math.PI * 2 / 7) + 0.3); leaf.translate(0, 0.34 + (i % 3) * 0.14, 0);
    parts.push(leaf);
  }
  const top = new THREE.ConeGeometry(0.075, 0.3, 5); top.translate(0, 0.74, 0); parts.push(top);
  const g = mergeGeometries(parts, false)!; for (const p of parts) p.dispose(); g.computeVertexNormals(); return g;
}
// A REAL-reading corn stalk (owner: the old cross of planes read as a weed): tall green stalk, arching
// blade leaves, two husked ears hugging the stalk with silk tufts, and a tan tassel spike on top.
function makeCorn(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const GREEN = 0x5f8f2e, HUSK = 0x7ba23f, SILK = 0xd9c26a, TASSEL = 0xcbb45a;
  const stalk = new THREE.CylinderGeometry(0.022, 0.045, 1.7, 6);
  tint(stalk, GREEN); stalk.translate(0, 0.85, 0); parts.push(stalk);
  // Arching blade leaves: long thin planes, drooping outward at alternating heights/angles.
  for (let i = 0; i < 5; i++) {
    const leaf = new THREE.PlaneGeometry(0.72, 0.085, 3, 1);
    const pos = leaf.getAttribute('position');
    for (let v = 0; v < pos.count; v++) { const x = pos.getX(v); pos.setY(v, pos.getY(v) - x * x * 0.55); } // droop curve
    tint(leaf, GREEN);
    leaf.rotateZ(0.35); leaf.rotateY(i * 2.4);
    leaf.translate(0, 0.55 + i * 0.22, 0);
    parts.push(leaf);
  }
  // Two ears hugging the stalk (angled up), wrapped in husk with a silk tuft at the tip.
  for (const [yaw, h] of [[0.7, 0.85], [3.6, 1.05]] as Array<[number, number]>) {
    const ear = new THREE.CylinderGeometry(0.055, 0.035, 0.34, 7);
    tint(ear, HUSK); ear.rotateZ(0.45); ear.rotateY(yaw);
    ear.translate(Math.sin(yaw) * 0.085, h, Math.cos(yaw) * 0.085); parts.push(ear);
    const silk = new THREE.ConeGeometry(0.02, 0.09, 5);
    tint(silk, SILK); silk.rotateZ(0.45); silk.rotateY(yaw);
    silk.translate(Math.sin(yaw) * 0.14, h + 0.19, Math.cos(yaw) * 0.14); parts.push(silk);
  }
  // Tassel: a slim tan spike crowning the stalk.
  const tas = new THREE.ConeGeometry(0.035, 0.3, 5);
  tint(tas, TASSEL); tas.translate(0, 1.83, 0); parts.push(tas);
  const g = mergeGeometries(parts, false)!; for (const p of parts) p.dispose(); g.computeVertexNormals(); return g;
}

export class ForageField {
  readonly group = new THREE.Group();
  private readonly defs: Record<ForageKind, KindDef>;
  private readonly meshes: Record<ForageKind, THREE.InstancedMesh>;
  private readonly visible: Record<ForageKind, number[]> = { mushroom: [], pumpkin: [], corn: [], coconut: [], apple: [], hemp: [] };
  private readonly grid = new Map<number, number[]>(); // slot indices by 48m cell (pickup queries + stream)
  private readonly MAX = FORAGE.count;
  private readonly fx = new Float32Array(this.MAX);
  private readonly fz = new Float32Array(this.MAX);
  private readonly fy = new Float32Array(this.MAX);
  private readonly fyaw = new Float32Array(this.MAX);
  private readonly fkind: ForageKind[] = [];
  private readonly alive: boolean[] = [];
  private readonly regrowAt: number[] = []; // seconds; when a collected slot comes back
  private count = 0;
  private streamCellX = NaN; private streamCellZ = NaN;
  private nowSec = 0;
  private rng = mulberry32(FORAGE_SEED);
  private readonly tmp = new THREE.Matrix4();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpScale = new THREE.Vector3(1, 1, 1);

  constructor() {
    this.defs = {
      mushroom: { item: 'mushroom', amount: 1, geom: makeMushroomCluster() },
      pumpkin: { item: 'pumpkin', amount: 1, geom: makePumpkin() },
      corn: { item: 'corn', amount: 2, geom: makeCorn() },
      coconut: { item: 'coconut', amount: 1, geom: makeCoconut() },
      apple: { item: 'apple', amount: 2, geom: makeApple() },
      hemp: { item: 'cloth', amount: 1, geom: makeHemp() }, // yields CLOTH (random 1-3, see collect())
    };
    const mat = (color: number, vcol = false): THREE.MeshStandardMaterial => { const m = new THREE.MeshStandardMaterial({ color, roughness: 0.85, metalness: 0, flatShading: true, vertexColors: vcol }); applyInteriorDark(m, true); return m; };
    // corn/coconut/apple are multi-coloured via vertex colours (see tint()); their material base stays white.
    this.meshes = {
      mushroom: new THREE.InstancedMesh(this.defs.mushroom.geom, mat(0xcaa274), FORAGE.cap),
      pumpkin: new THREE.InstancedMesh(this.defs.pumpkin.geom, mat(0xe0822c), FORAGE.cap),
      corn: new THREE.InstancedMesh(this.defs.corn.geom, mat(0xffffff, true), FORAGE.cap),
      coconut: new THREE.InstancedMesh(this.defs.coconut.geom, mat(0xffffff, true), FORAGE.cap),
      apple: new THREE.InstancedMesh(this.defs.apple.geom, mat(0xffffff, true), FORAGE.cap),
      hemp: new THREE.InstancedMesh(this.defs.hemp.geom, mat(0x4f7a34), FORAGE.cap),
    };
    for (const k of Object.keys(this.meshes) as ForageKind[]) { const m = this.meshes[k]; m.castShadow = true; m.receiveShadow = true; m.count = 0; this.group.add(m); }
    this.fill();
  }

  regenerate(): void { this.fill(); }

  private place(x: number, z: number, kind: ForageKind): boolean {
    if (this.count >= this.MAX) return false;
    const idx = this.count++;
    this.fx[idx] = x; this.fz[idx] = z; this.fy[idx] = terrainHeight(x, z); this.fyaw[idx] = this.rng() * Math.PI * 2;
    this.fkind[idx] = kind; this.alive[idx] = true; this.regrowAt[idx] = 0;
    const key = (Math.floor(x / F_CELL) + F_OFF) * F_STRIDE + (Math.floor(z / F_CELL) + F_OFF);
    let b = this.grid.get(key); if (!b) { b = []; this.grid.set(key, b); } b.push(idx);
    return true;
  }

  // Plants grow in CLUSTERS on a JITTERED GRID across the whole island, so coverage is EVEN everywhere -- near
  // spawn and out to the coast alike -- instead of random scatter that leaves bare patches (a player spawning
  // in a gap saw no food). Each grid cell that lands on a forage habitat drops a patch, its kind chosen by the
  // LOCAL biome (waterside/pasture -> pumpkin+corn, forest/grass -> mushrooms), then a random top-up keeps the
  // total at MAX when many cells were ocean/rock.
  private fill(): void {
    this.rng = mulberry32(FORAGE_SEED);
    this.grid.clear(); this.count = 0; this.fkind.length = 0; this.alive.length = 0; this.regrowAt.length = 0;
    // Out to the COAST (islandOuter), not just islandInner: the player spawns on the outer beach (~radius
    // islandOuter), well past islandInner, so forage must reach the coastal grasslands or spawn is foodless.
    // Cells that land on beach/ocean drop nothing (kindAt rejects them), so the extra radius is free.
    const R = TERRAIN.islandOuter;
    const spacing = 66; // ~66m between cluster centres (was 82) -> more patches inside any draw radius, denser + more evenly spread food
    // 1) Collect a jittered-grid candidate cluster centre for every habitable cell across the whole island.
    const cand: Array<[number, number, ForageKind]> = [];
    for (let gz = -R; gz <= R; gz += spacing) {
      for (let gx = -R; gx <= R; gx += spacing) {
        const cx = gx + (this.rng() - 0.5) * spacing * 0.9; // jitter inside the cell so the grid never reads as rows
        const cz = gz + (this.rng() - 0.5) * spacing * 0.9;
        if (cx * cx + cz * cz > R * R) continue; // outside the island land radius
        const kind = this.kindAt(cx, cz);
        if (kind) cand.push([cx, cz, kind]);
      }
    }
    // 2) Shuffle (deterministic Fisher-Yates) so that when candidates exceed the buffer we truncate EVENLY
    // across the map -- NOT bottom-up, which starved the far (spawn) side of the island and left it foodless.
    for (let i = cand.length - 1; i > 0; i--) { const j = (this.rng() * (i + 1)) | 0; const t = cand[i]; cand[i] = cand[j]; cand[j] = t; }
    // 3) Drop patches in shuffled order until the buffer fills -> uniform density everywhere, spawn included.
    for (let i = 0; i < cand.length && this.count < this.MAX; i++) this.dropPatch(cand[i][0], cand[i][1], cand[i][2]);
    for (const k of Object.keys(this.meshes) as ForageKind[]) { this.meshes[k].count = 0; this.visible[k].length = 0; }
    this.streamCellX = NaN; this.streamCellZ = NaN;
  }

  // The forage kind that grows at a point, or null if it's not a habitat (water/sand/rock/snow/steep). Each
  // kind has a HABITAT but all three are COMMON so you find variety: pumpkin/corn on riverbanks/lakesides +
  // open pastures (meadow) + some plains; mushrooms on the forest floor + denser grassland. (The old gate
  // required inland water for pumpkin/corn -- which barely exists -- so 97% spawned as mushrooms.)
  private kindAt(x: number, z: number): ForageKind | null {
    const h = terrainHeight(x, z);
    if (h <= TERRAIN.seaLevel + 0.5) return null; // in the water
    const b = biomeAt(x, z, h);
    const r = this.rng();
    // COCONUTS where PALMS grow: the beach + desert coast. Allowed nearer the waterline than the other crops
    // (palms line the shore), so coconuts read as "washed up / dropped at the palm's foot".
    if (b === Biome.Beach || b === Biome.Desert) return r < 0.6 ? 'coconut' : null;
    if (h <= TERRAIN.seaLevel + 2) return null; // the other crops stay off the immediate shoreline
    if (this.nearInlandWater(x, z)) return r < 0.5 ? 'pumpkin' : 'corn';            // waterside crops
    if (b === Biome.Meadow) return r < 0.4 ? 'pumpkin' : (r < 0.68 ? 'corn' : (r < 0.86 ? 'hemp' : 'mushroom')); // pasture + hemp
    // FOREST floor: mostly mushrooms, with RARE fallen APPLES (apple TREES also carry red markers -- see nodes.ts) + some hemp.
    if (b === Biome.Forest) return r < 0.06 ? 'apple' : (r < 0.58 ? 'mushroom' : (r < 0.74 ? 'hemp' : (r < 0.88 ? 'pumpkin' : 'corn')));
    if (b === Biome.Grassland) return r < 0.36 ? 'mushroom' : (r < 0.57 ? 'hemp' : (r < 0.8 ? 'pumpkin' : 'corn')); // plains mix + hemp
    return null;
  }

  // Drop one patch of `kind` around (cx,cz): a handful of plants scattered within the patch radius.
  private dropPatch(cx: number, cz: number, kind: ForageKind): void {
    const per = kind === 'mushroom' ? 4 + ((this.rng() * 5) | 0) : 3 + ((this.rng() * 4) | 0); // patch size
    const spread = kind === 'mushroom' ? 4 : 7;
    for (let j = 0; j < per && this.count < this.MAX; j++) {
      const a = this.rng() * Math.PI * 2, r = Math.sqrt(this.rng()) * spread;
      const x = cx + Math.cos(a) * r, z = cz + Math.sin(a) * r;
      if (terrainHeight(x, z) > TERRAIN.seaLevel + 2) this.place(x, z, kind);
    }
  }

  // TEST: drop a visible patch of every kind a few metres in front of (px,pz) so all of it can be tried at spawn.
  seedNear(px: number, pz: number): void {
    const kinds: ForageKind[] = ['mushroom', 'pumpkin', 'corn'];
    kinds.forEach((kind, ki) => {
      for (let j = 0; j < 5; j++) {
        const ang = (j / 5) * Math.PI * 2;
        const x = px + (ki - 1) * 6 + Math.cos(ang) * 2;
        const z = pz + 6 + Math.sin(ang) * 2;
        if (terrainHeight(x, z) > TERRAIN.seaLevel) this.place(x, z, kind);
      }
    });
    this.streamCellX = NaN; // force a re-stream so they show immediately
  }

  // Inland (not ocean) water within ~14m -> a riverbank/lakeside spot for pumpkin/corn.
  private nearInlandWater(x: number, z: number): boolean {
    if (Math.hypot(x, z) > TERRAIN.islandInner * 0.92) return false;
    const sea = TERRAIN.seaLevel;
    for (let a = 0; a < 6.28; a += 1.05) if (terrainHeight(x + Math.cos(a) * 14, z + Math.sin(a) * 14) < sea - 0.5) return true;
    return false;
  }

  // Nearest collectable plant within `range` of (px,pz), or -1. Uses the grid (cheap).
  nearestPickable(px: number, pz: number, range: number): number {
    const r2 = range * range; let best = -1, bestD = r2;
    const cx0 = Math.floor((px - range) / F_CELL), cx1 = Math.floor((px + range) / F_CELL);
    const cz0 = Math.floor((pz - range) / F_CELL), cz1 = Math.floor((pz + range) / F_CELL);
    for (let cx = cx0; cx <= cx1; cx++) for (let cz = cz0; cz <= cz1; cz++) {
      const bucket = this.grid.get((cx + F_OFF) * F_STRIDE + (cz + F_OFF)); if (!bucket) continue;
      for (const i of bucket) { if (!this.alive[i]) continue; const dx = this.fx[i] - px, dz = this.fz[i] - pz, dd = dx * dx + dz * dz; if (dd < bestD) { bestD = dd; best = i; } }
    }
    return best;
  }

  // The item a slot yields (for the context prompt label), or null.
  itemAt(i: number): ItemId | null { return i >= 0 && i < this.count ? this.defs[this.fkind[i]].item : null; }

  // Where a slot's plant stands (for the network relay), or null.
  posAt(i: number): { x: number; z: number } | null { return i >= 0 && i < this.count ? { x: this.fx[i], z: this.fz[i] } : null; }

  // Collect a plant: hide it, start its regrow timer, return what it yields.
  collect(i: number): { item: ItemId; amount: number } | null {
    if (i < 0 || i >= this.count || !this.alive[i]) return null;
    const def = this.defs[this.fkind[i]];
    this.alive[i] = false;
    this.regrowAt[i] = this.nowSec + FORAGE.regrowSec;
    this.streamCellX = NaN; // force a re-stream so it disappears promptly
    // Hemp gives CLOTH at a random 1-3 per plant, with 3 the rare roll.
    const amount = this.fkind[i] === 'hemp' ? (() => { const r = Math.random(); return r < 0.55 ? 1 : r < 0.9 ? 2 : 3; })() : def.amount;
    return { item: def.item, amount };
  }

  // SERVER-AUTHORITATIVE pickup: mark the plant nearest (x,z) as picked and set its regrow from the server-supplied
  // remaining seconds (so every client's regrow lands at the SAME moment, not on an independent local clock). Used by
  // the MP welcome (a plant a teammate picked stays gone for a late joiner) + relog. Tight radius = the exact plant.
  markPickedAt(x: number, z: number, remainingSec: number): void {
    let best = -1, bestD = 0.36; // (0.6m)^2 -- distinct plants are far apart, so this hits the exact one
    for (let i = 0; i < this.count; i++) {
      if (!this.alive[i]) continue;
      const dx = this.fx[i] - x, dz = this.fz[i] - z, dd = dx * dx + dz * dz;
      if (dd < bestD) { bestD = dd; best = i; }
    }
    if (best < 0) return;
    this.alive[best] = false;
    this.regrowAt[best] = this.nowSec + Math.max(1, remainingSec); // shared respawn deadline from the server
    this.streamCellX = NaN; // re-stream so it disappears here too
  }

  // World save: only the DELTA from the deterministic layout (picked, still-regrowing slots).
  serialize(): SavedForage[] {
    const out: SavedForage[] = [];
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i]) continue;
      out.push({ k: fKey(this.fx[i], this.fz[i]), rem: Math.max(1, this.regrowAt[i] - this.nowSec) });
    }
    return out;
  }

  // Re-apply picked state on boot (before the first frame). nowSec is still ~0 here, so the restored
  // regrow deadline is simply the remaining seconds on the fresh clock. Unmatched keys are dropped:
  // a reshaped layout means that plant no longer exists, and a fresh one standing there is harmless.
  deserialize(data: SavedForage[]): void {
    const rem = new Map<string, number>();
    for (const e of data) if (e.rem > 0) rem.set(e.k, e.rem);
    for (let i = 0; i < this.count && rem.size > 0; i++) {
      const r = rem.get(fKey(this.fx[i], this.fz[i]));
      if (r === undefined) continue;
      this.alive[i] = false;
      this.regrowAt[i] = this.nowSec + r;
      rem.delete(fKey(this.fx[i], this.fz[i]));
    }
    this.streamCellX = NaN; // re-stream so restored gaps show from frame one
  }

  // Per-frame: advance the regrow clock (revive ripe slots) + stream nearby plants.
  update(camX: number, camZ: number, nowSec: number): void {
    this.nowSec = nowSec;
    let revived = false;
    for (let i = 0; i < this.count; i++) if (!this.alive[i] && this.regrowAt[i] > 0 && nowSec >= this.regrowAt[i]) { this.alive[i] = true; this.regrowAt[i] = 0; revived = true; }
    const cellX = Math.floor(camX / NODES.streamCell), cellZ = Math.floor(camZ / NODES.streamCell);
    if (revived || cellX !== this.streamCellX || cellZ !== this.streamCellZ) { this.streamCellX = cellX; this.streamCellZ = cellZ; this.streamRebuild(camX, camZ); }
  }

  private streamRebuild(camX: number, camZ: number): void {
    const R = NODES.drawRadius, r2 = R * R;
    for (const k of Object.keys(this.visible) as ForageKind[]) this.visible[k].length = 0;
    const cx0 = Math.floor((camX - R) / F_CELL), cx1 = Math.floor((camX + R) / F_CELL);
    const cz0 = Math.floor((camZ - R) / F_CELL), cz1 = Math.floor((camZ + R) / F_CELL);
    for (let cx = cx0; cx <= cx1; cx++) for (let cz = cz0; cz <= cz1; cz++) {
      const bucket = this.grid.get((cx + F_OFF) * F_STRIDE + (cz + F_OFF)); if (!bucket) continue;
      for (const i of bucket) {
        if (!this.alive[i]) continue;
        const dx = this.fx[i] - camX, dz = this.fz[i] - camZ; if (dx * dx + dz * dz > r2) continue;
        const k = this.fkind[i], vis = this.visible[k], slot = vis.length; if (slot >= FORAGE.cap) continue;
        this.tmpPos.set(this.fx[i], this.fy[i], this.fz[i]); this.tmpQuat.setFromAxisAngle(UP, this.fyaw[i]);
        this.tmp.compose(this.tmpPos, this.tmpQuat, this.tmpScale); this.meshes[k].setMatrixAt(slot, this.tmp); vis.push(i);
      }
    }
    for (const k of Object.keys(this.meshes) as ForageKind[]) { const m = this.meshes[k]; m.count = this.visible[k].length; m.instanceMatrix.needsUpdate = true; m.computeBoundingSphere(); }
  }
}
